import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type {
  ApiResponse,
  User,
  LoginInput,
  RegisterInput,
  AuthResponse,
  Tokens,
} from '@/types';
import { useAuthStore } from '@/stores';
import { useNavigate } from 'react-router-dom';

// Login
export function useLogin() {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LoginInput): Promise<AuthResponse> => {
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Login failed');
      }
      return response.data.data;
    },
    onSuccess: (data) => {
      // Only set auth if not requiring 2FA
      if (!data.requiresTwoFactor && data.user) {
        setAuth(data.user, {
          access_token: data.access_token!,
          refresh_token: data.refresh_token!,
          expires_in: data.expires_in!,
        });
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      }
    },
  });
}

// Register
export function useRegister() {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: RegisterInput): Promise<AuthResponse> => {
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/register', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Registration failed');
      }
      return response.data.data;
    },
    onSuccess: (data) => {
      setAuth(data.user, {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
      });
      // Navigate after state is updated
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 0);
    },
  });
}

// Logout
export function useLogout() {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      await api.post('/auth/logout', { refresh_token: refreshToken });
    },
    onSettled: () => {
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });
}

// Get current user
export function useCurrentUser() {
  const { isAuthenticated, setUser } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async (): Promise<User> => {
      const response = await api.get<ApiResponse<{ user: User }>>('/auth/me');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to get user');
      }
      const user = response.data.data.user;
      setUser(user);
      return user;
    },
    enabled: isAuthenticated,
    retry: false,
  });
}

// Refresh token
export function useRefreshToken() {
  const { setTokens } = useAuthStore();

  return useMutation({
    mutationFn: async (refreshToken: string): Promise<Tokens> => {
      const response = await api.post<ApiResponse<Tokens>>('/auth/refresh', {
        refresh_token: refreshToken,
      });
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Token refresh failed');
      }
      return response.data.data;
    },
    onSuccess: (data) => {
      setTokens(data);
    },
  });
}

// Update profile
export function useUpdateProfile() {
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { username?: string }): Promise<User> => {
      const response = await api.patch<ApiResponse<{ user: User }>>('/auth/me', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Update failed');
      }
      return response.data.data.user;
    },
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

// Change password
export function useChangePassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: { current_password: string; new_password: string }) => {
      const response = await api.post<ApiResponse>('/auth/me/password', data);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Password change failed');
      }
      return response.data;
    },
    onSuccess: () => {
      // Redirect to login after password change
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth-storage');
      navigate('/login');
    },
  });
}
